import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Event, EventDateOption } from "@family/shared";
import { apiFetch } from "../lib/api";
import { APP_LOCALE } from "../lib/dates";

/** "Sat 12 June 2027" — the weekday matters when picking a date. */
function formatOption(date: string): string {
  return new Date(date).toLocaleDateString(APP_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface EventDatePollProps {
  slug: string;
  event: Event;
  canVote: boolean;
  canManage: boolean;
}

/**
 * The optional "help us pick a date" poll on an undated event. Members tick
 * every date they can make; an organiser picks the winner, which sets the
 * event's date and closes the poll.
 */
export function EventDatePoll({ slug, event, canVote, canManage }: EventDatePollProps) {
  const queryClient = useQueryClient();

  function onUpdated(updated: Event) {
    queryClient.setQueryData<Event>(["event", slug, event.id], updated);
    void queryClient.invalidateQueries({ queryKey: ["events", slug] });
  }

  const vote = useMutation<Event, Error, { optionId: string; vote: boolean }>({
    mutationFn: (body) =>
      apiFetch<Event>(`/families/${slug}/events/${event.id}/date-votes`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: onUpdated,
  });

  const pick = useMutation<Event, Error, string>({
    mutationFn: (date) =>
      apiFetch<Event>(`/families/${slug}/events/${event.id}`, {
        method: "PATCH",
        body: JSON.stringify({ eventDate: date }),
      }),
    onSuccess: onUpdated,
  });

  const options = event.dateOptions;
  if (event.eventDate || options.length === 0) return null;

  const top = Math.max(...options.map((o) => o.votes));
  const busy = vote.isPending || pick.isPending;

  return (
    <div className="card stack" style={{ gap: "var(--s-3)" }}>
      <div>
        <strong>Help pick a date</strong>
        <div className="text-muted" style={{ fontSize: "0.88rem", marginTop: 4 }}>
          The date isn’t set yet. Tick every date you could make.
        </div>
      </div>

      <ul className="list-unstyled stack" style={{ gap: "var(--s-2)" }}>
        {options.map((o) => (
          <OptionRow
            key={o.id}
            option={o}
            leading={top > 0 && o.votes === top}
            canVote={canVote}
            canManage={canManage}
            disabled={busy}
            onVote={(v) => vote.mutate({ optionId: o.id, vote: v })}
            onPick={() => {
              if (
                window.confirm(
                  `Set the date to ${formatOption(o.date)}? This closes the vote.`,
                )
              ) {
                pick.mutate(o.date);
              }
            }}
          />
        ))}
      </ul>

      {(vote.error || pick.error) && (
        <p className="text-error">{(vote.error ?? pick.error)!.message}</p>
      )}
    </div>
  );
}

function OptionRow({
  option,
  leading,
  canVote,
  canManage,
  disabled,
  onVote,
  onPick,
}: {
  option: EventDateOption;
  leading: boolean;
  canVote: boolean;
  canManage: boolean;
  disabled: boolean;
  onVote: (vote: boolean) => void;
  onPick: () => void;
}) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--s-3)",
        padding: "8px 0",
        borderBottom: "1px solid var(--c-border)",
        flexWrap: "wrap",
      }}
    >
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flex: 1,
          minWidth: 180,
          cursor: canVote ? "pointer" : "default",
        }}
      >
        {canVote && (
          <input
            type="checkbox"
            checked={option.votedByMe}
            onChange={(e) => onVote(e.target.checked)}
            disabled={disabled}
            style={{ width: "auto", minHeight: 0, padding: 0, margin: 0 }}
          />
        )}
        <span style={{ fontWeight: leading ? 700 : 500 }}>
          {formatOption(option.date)}
        </span>
      </label>
      <span
        className={`badge ${leading ? "badge--admin" : ""}`}
        title={option.voters.join(", ") || "No votes yet"}
      >
        {option.votes} {option.votes === 1 ? "vote" : "votes"}
      </span>
      {canManage && (
        <button
          type="button"
          className="btn-tertiary"
          onClick={onPick}
          disabled={disabled}
        >
          Pick this date
        </button>
      )}
      {option.voters.length > 0 && (
        <div
          className="text-muted"
          style={{ fontSize: "0.8rem", flexBasis: "100%" }}
        >
          {option.voters.join(", ")}
        </div>
      )}
    </li>
  );
}
